import React from 'react';
import { useNavigate } from 'react-router-dom';
import PageSEO from '../components/PageSEO';
import {
  ArrowLeft,
  ShieldCheck,
  Users,
  Award,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  TrendingDown,
  Info,
} from 'lucide-react';
import '../styles/cancellation-policy.css';

const CancellationPolicy = () => {
  const navigate = useNavigate();

  return (
    <>
      <PageSEO
        title="Cancellation Policy"
        description="taskeri® cancellation rules for clients and Task Heroes: notice windows, cancellation fees, refunds from escrow, no-show penalties and how to appeal."
        path="/cancellation-policy"
      />
    <div className="cancel-policy-page" data-testid="cancellation-policy-page">
      <div className="cancel-policy-page__inner">
        <button
          className="cancel-policy-page__back"
          onClick={() => navigate(-1)}
          data-testid="cancellation-policy-back"
        >
          <ArrowLeft size={16} />
          Back
        </button>

        <header className="cancel-policy-page__hero">
          <div className="cancel-policy-page__hero-icon">
            <ShieldCheck size={36} />
          </div>
          <h1>Cancellation Policy</h1>
          <p>
            Plans change — we get it. Here's exactly what happens when a booking is cancelled,
            whether you're a <strong>client</strong> or a <strong>hero</strong>. We aim for the rules
            to be predictable, fair to both sides, and tougher the later the cancellation is.
          </p>
        </header>

        {/* ============ CLIENT POLICY ============ */}
        <section className="cancel-policy-card client" data-testid="cancellation-policy-client">
          <header className="cancel-policy-card__header">
            <Users size={22} />
            <div>
              <h2>For Clients</h2>
              <p>When you cancel a job you've booked</p>
            </div>
          </header>

          <table className="cancel-policy-table">
            <thead>
              <tr>
                <th>When you cancel</th>
                <th>Refund to you</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Before the hero accepts</strong> (selection stage)</td>
                <td className="ok">100%</td>
                <td>Free cancel — the hero hasn't committed yet.</td>
              </tr>
              <tr>
                <td><strong>Within 1 hour after the hero accepts</strong></td>
                <td className="ok">100%</td>
                <td>Acceptance grace period — change of mind is fine.</td>
              </tr>
              <tr>
                <td>More than <strong>24 hours</strong> before the scheduled time</td>
                <td className="ok">100% <small>(minus platform fee)</small></td>
                <td>Plenty of notice — hero has time to rebook.</td>
              </tr>
              <tr>
                <td><strong>2–24 hours</strong> before scheduled time</td>
                <td className="warn">25%</td>
                <td>75% goes to the hero as compensation.</td>
              </tr>
              <tr>
                <td><strong>Less than 2 hours</strong> before scheduled time</td>
                <td className="bad">0%</td>
                <td>No refund — hero has likely organised their day around you.</td>
              </tr>
              <tr>
                <td><strong>ASAP jobs</strong> (after the 1-hour acceptance grace)</td>
                <td className="bad">0%</td>
                <td>No refund — the hero may already be en route.</td>
              </tr>
            </tbody>
          </table>

          <div className="cancel-policy-callout success">
            <CheckCircle size={18} />
            <span>
              <strong>If the hero cancels, you always receive a full refund</strong> — there are no
              circumstances where a hero's cancellation costs you money.
            </span>
          </div>
        </section>

        {/* ============ HERO POLICY ============ */}
        <section className="cancel-policy-card hero" data-testid="cancellation-policy-hero">
          <header className="cancel-policy-card__header">
            <Award size={22} />
            <div>
              <h2>For Heroes</h2>
              <p>When you cancel a job you've accepted</p>
            </div>
          </header>

          <p className="cancel-policy-card__intro">
            The client always receives a <strong>100% refund</strong>. You'll incur a penalty
            (deducted from your next transfer) that scales with how late you cancel and how often
            you've cancelled recently.
          </p>

          <table className="cancel-policy-table">
            <thead>
              <tr>
                <th>Stage when you cancel</th>
                <th>Penalty</th>
                <th>Why</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Before en route / mid-work</td>
                <td><strong>1×</strong> platform fee</td>
                <td>Earliest stage, lowest impact on client.</td>
              </tr>
              <tr>
                <td>After marking <em>en route</em></td>
                <td><strong>1.5×</strong> platform fee</td>
                <td>Client is already waiting.</td>
              </tr>
              <tr>
                <td>Once work is <em>in progress</em></td>
                <td><strong>2×</strong> platform fee</td>
                <td>The job is partially done — significant disruption.</td>
              </tr>
            </tbody>
          </table>

          <div className="cancel-policy-callout warn">
            <AlertTriangle size={18} />
            <div>
              <strong>Repeat cancellations escalate the penalty</strong>
              <ul className="cancel-policy-callout__list">
                <li><strong>2nd cancel</strong> within 30 days → penalty multiplied by <strong>×1.5</strong></li>
                <li><strong>3rd+ cancel</strong> within 30 days → penalty multiplied by <strong>×2</strong></li>
              </ul>
            </div>
          </div>

          <div className="cancel-policy-callout danger">
            <TrendingDown size={18} />
            <span>
              Cancellations also affect your <strong>reliability score</strong>, which can lower your
              ranking in search, reduce your match priority, and impact your rating.
            </span>
          </div>

          <div className="cancel-policy-callout info">
            <Info size={18} />
            <span>
              If you genuinely can't complete a job, cancelling is the right call — but please
              <strong> message the client first</strong> when possible. Communication often saves
              the booking (and avoids the penalty).
            </span>
          </div>
        </section>

        {/* ============ SHARED ============ */}
        <section className="cancel-policy-card neutral">
          <header className="cancel-policy-card__header">
            <Clock size={22} />
            <div>
              <h2>How it's calculated</h2>
              <p>Quick reference for both sides</p>
            </div>
          </header>
          <ul className="cancel-policy-list-bullets">
            <li>The <strong>platform fee</strong> is the service-fee portion shown on every job receipt (it's the same fee that's added on top of the bid amount when a client books).</li>
            <li>The <strong>scheduled time</strong> is the appointment time agreed when the booking was confirmed. For ASAP jobs (no scheduled time), the late-cancel tiers don't apply — the job is treated as imminent.</li>
            <li>Before you confirm a cancellation in the app, the modal shows the exact dollar amounts (refund / penalty) so there are no surprises.</li>
            <li>The hero's repeat-cancellation count covers a <strong>rolling 30-day window</strong> of <em>hero-initiated</em> cancellations only — declines made before accepting don't count.</li>
          </ul>
        </section>

        <p className="cancel-policy-page__foot">
          Have a question or think a cancellation was handled incorrectly? Open a dispute on the job
          page or reach out to support. We review each case individually.
        </p>
      </div>
    </div>
    </>
  );
};

export default CancellationPolicy;
